import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import {
  CAPTURED_VIA,
  capturedViaLabel,
  consentApi,
  consentQueryKeys,
  type ConsentTextVersion,
  type ScopeOption,
} from "../../shared/api/consent";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  PatientCombobox,
  type PatientChoice,
} from "../appointments/components/PatientCombobox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * The document the chosen scope is recorded against — never "the newest one" (#18618/#18912).
 *
 * <p>The staff member reads this aloud, or prints it, before the patient agrees. Picking it by
 * recency is the same false statement as recording it by recency, one step earlier in the same
 * conversation and at the only step a patient can actually see.
 *
 * <p>`textCode` comes from the server (`GET /consent/scopes`) rather than a copy of
 * `ConsentTextCatalog` kept here, so the screen and the capture cannot drift apart. `texts` arrives
 * newest-first, so `find` yields the in-force version of that document — the same row
 * `ConsentRepository.currentTextVersionIdForCode` will bind the record to.
 */
export function documentForScope(
  scopes: ScopeOption[],
  texts: ConsentTextVersion[],
  scope: string
): ConsentTextVersion | null {
  const option = scopes.find((s) => s.code === scope);
  if (!option?.textCode) return null;
  return texts.find((t) => t.code === option.textCode) ?? null;
}

/**
 * T90 — Quản lý đồng thuận (Nghị định 13/2023/NĐ-CP). Real /api/v1/consent.
 * Pick a patient → see their consent trail (append-only; withdrawal never
 * deletes the grant), capture new consent, withdraw, and view/print the
 * document THAT scope is recorded against — the one the server will bind the
 * record to, resolved by scope rather than by recency (#18618/#18912).
 */
export function ConsentPage() {
  const qc = useQueryClient();
  const [patient, setPatient] = useState<PatientChoice | null>(null);
  const [scope, setScope] = useState("");
  const [via, setVia] = useState("IN_PERSON");
  const [error, setError] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);

  const scopes = useQuery({
    queryKey: consentQueryKeys.scopes(),
    queryFn: () => consentApi.scopes(),
  });
  const texts = useQuery({
    queryKey: consentQueryKeys.textVersions(),
    queryFn: () => consentApi.textVersions(),
  });
  const list = useQuery({
    queryKey: consentQueryKeys.list(patient?.id ?? ""),
    queryFn: () => consentApi.list(patient!.id),
    enabled: !!patient,
  });

  const capture = useMutation({
    mutationFn: () => consentApi.capture(patient!.id, scope, via),
    onSuccess: () => {
      setScope("");
      setError(null);
      qc.invalidateQueries({ queryKey: consentQueryKeys.list(patient!.id) });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) =>
      consentApi.withdraw(
        id,
        window.prompt("Lý do rút lại đồng thuận?") || ""
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: consentQueryKeys.list(patient!.id) }),
  });

  const selected = (scopes.data ?? []).find((s) => s.code === scope) ?? null;
  const scopeText = documentForScope(scopes.data ?? [], texts.data ?? [], scope);

  return (
    <div>
      <PageHeader
        title="Quản lý đồng thuận"
        description="Đồng thuận xử lý dữ liệu cá nhân của bệnh nhân theo Nghị định 13/2023/NĐ-CP. Bản ghi không bị xoá — rút lại chỉ thêm dấu thu hồi."
        actions={
          <Button asChild variant="secondary">
            <Link to="/settings/audit-log">
              Nhật ký kiểm toán
              <ArrowRight />
            </Link>
          </Button>
        }
      />

      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid max-w-lg gap-1.5">
            <Label>Bệnh nhân</Label>
            <PatientCombobox value={patient} onChange={setPatient} />
          </div>
        </CardContent>
      </Card>

      {patient && (
        <>
          <Card className="mb-4">
            <CardHeader className="p-4 pb-0">
              <CardTitle className="text-base">
                Ghi nhận đồng thuận mới
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid items-end gap-3 sm:grid-cols-[2fr_1fr_auto]">
                <div className="grid gap-1.5">
                  <Label>Phạm vi đồng thuận</Label>
                  <Select
                    value={scope || undefined}
                    onValueChange={(v) => setScope(v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="— Chọn phạm vi —" />
                    </SelectTrigger>
                    <SelectContent>
                      {(scopes.data ?? []).map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Hình thức</Label>
                  <Select value={via} onValueChange={(v) => setVia(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CAPTURED_VIA.map((v) => (
                        <SelectItem key={v.value} value={v.value}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  disabled={!scope || !scopeText || capture.isPending}
                  onClick={() => {
                    setError(null);
                    capture.mutate();
                  }}
                >
                  {capture.isPending ? "Đang ghi…" : "Ghi nhận"}
                </Button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-destructive">{error}</p>
              )}

              {selected && !scopeText && !texts.isLoading && (
                <p className="mt-3 text-sm text-destructive">
                  Chưa có văn bản đồng thuận hiệu lực cho phạm vi này
                  {selected.textCode ? ` (mã ${selected.textCode})` : ""}. Không
                  ghi nhận được đồng thuận cho tới khi quản trị cấu hình văn bản
                  theo Nghị định 13.
                </p>
              )}

              {scopeText && (
                <div className="mt-3 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <strong>
                      Văn bản bệnh nhân đọc: {scopeText.code} ·{" "}
                      {scopeText.version}
                    </strong>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowText((v) => !v)}
                    >
                      {showText ? "Ẩn nội dung" : "Xem / in văn bản"}
                    </Button>
                  </div>
                  {scopeText.version.endsWith("-DRAFT") && (
                    <p className="mt-2 text-sm text-destructive">
                      Bản DỰ THẢO, chưa qua rà soát pháp lý — dùng cho dev/test.
                    </p>
                  )}
                  {showText && (
                    <pre className="mt-3 whitespace-pre-wrap rounded-md bg-muted p-3 font-sans text-[13px] leading-relaxed text-foreground">
                      {scopeText.bodyVi}
                    </pre>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phạm vi</TableHead>
                  <TableHead>Hình thức</TableHead>
                  <TableHead>Thời điểm</TableHead>
                  <TableHead>Mẫu</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      Đang tải…
                    </TableCell>
                  </TableRow>
                )}
                {!list.isLoading && (list.data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      Bệnh nhân chưa có bản ghi đồng thuận.
                    </TableCell>
                  </TableRow>
                )}
                {(list.data ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      {scopes.data?.find((s) => s.code === c.scope)?.label ??
                        c.scope}
                    </TableCell>
                    <TableCell>{capturedViaLabel(c.capturedVia)}</TableCell>
                    <TableCell>
                      {new Date(c.grantedAt).toLocaleString("vi-VN")}
                    </TableCell>
                    <TableCell className="font-mono">
                      {c.textCode ? `${c.textCode}·${c.textVersion}` : "—"}
                    </TableCell>
                    <TableCell>
                      {c.withdrawn ? (
                        <Badge variant="destructive">Đã rút lại</Badge>
                      ) : (
                        <Badge variant="success">Đang hiệu lực</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {!c.withdrawn && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => withdraw.mutate(c.id)}
                        >
                          Rút lại
                        </Button>
                      )}
                      {c.withdrawn && c.withdrawReason && (
                        <span className="text-xs text-muted-foreground">
                          {c.withdrawReason}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
